import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ClerkProvider } from '@clerk/react'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import Dashboard from './routes/Dashboard.tsx'
import Privacy from './routes/Privacy.tsx'
import Record from './routes/Record.tsx'
import StaffChat from './routes/StaffChat.tsx'
import StaffConversationGuard from './components/StaffConversationGuard.tsx'
import OwnerQuestions from './routes/OwnerQuestions.tsx'
import OwnerDocuments from './routes/OwnerDocuments.tsx'
import OwnerFacts from './routes/OwnerFacts.tsx'
import OwnerRenewals from './routes/OwnerRenewals.tsx'
import OwnerReport from './routes/OwnerReport.tsx'
import { env } from './lib/env.ts'
import { getActivePod } from './lib/pod.ts'
import './index.css'
// Apply the active pod's palette to :root BEFORE the first render. index.css
// carries baked-in defaults only; the pod's theme map is the source of truth,
// so no pod can render in another pod's colours (Batch C — completes the
// "all colour comes from the pod" contract that was previously words-only).
{
  const root = document.documentElement
  for (const [prop, value] of Object.entries(getActivePod().theme)) {
    root.style.setProperty(prop, value)
  }
}
// Label the document from the ACTIVE POD, never from the static index.html.
// The shell's <title> is only a neutral pre-boot fallback; the pod's brand is
// the source of truth, so each deployment names itself (Foredesk, Hall's
// Roofing, Oxydise) and no pod's wording can leak onto another. Same contract
// as the theme block above, applied to title + description.
{
  const pod = getActivePod()
  // THE RECORD PAGE IS NOT POD-BRANDED, and must not be.
  //
  // Every other route belongs to one pod, so labelling the document from the
  // active pod is right. The record page does not: it is opened by a SUPPLIER
  // from a text message, and the deployment he opens it on carries whichever pod
  // that Pages project was built for. Left alone, a roofer answering a question
  // would see another business's name in his tab — the exact brand-leak the pod
  // contract exists to prevent, and spotted in production on 28 Aug 2026.
  //
  // Checked against the PATH rather than the router, because this runs before
  // React mounts.
  const isRecordPage = window.location.pathname.startsWith('/record/')
  document.title = isRecordPage ? 'Answer by voice' : `${pod.brandName} — ${pod.roleLabel}`
  let desc = document.querySelector('meta[name="description"]')
  if (!desc) {
    desc = document.createElement('meta')
    desc.setAttribute('name', 'description')
    document.head.appendChild(desc)
  }
  desc.setAttribute(
    'content',
    isRecordPage ? 'Record a spoken answer for your assistant.' : pod.tagline,
  )
}
// Initialise Sentry only when a DSN is provided. beforeSend scrubbing is added
// with the dashboard data flows in a later phase per Build Standard Section 8.
if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    tracesSampleRate: 0,
  })
}
/**
 * Routes:
 *   /               public chat (NO login) — the Phase 2 assistant, unchanged.
 *   /record/:token  voice capture (NO login) — Build 1. The link token IS the
 *                   authentication, so this route is Clerk-free by construction,
 *                   exactly like the public chat: no auth JS, no token calls.
 *                   It is also the ONLY path granted a microphone, scoped in
 *                   public/_headers.
 *   /dashboard      supplier dashboard (login required, invite-only via Clerk).
 *
 * ClerkProvider is scoped to the /dashboard route ONLY (rendered deeper in the
 * tree, per Clerk's guidance), NOT around the whole app. This keeps the public
 * buyer chat completely Clerk-free: it loads no auth JS and makes no token
 * calls — the frictionless, no-login experience the product depends on. Auth
 * context exists exactly where it's needed (the dashboard) and nowhere else.
 *
 * BrowserRouter stays at the top so client-side routing (and the in-app Links
 * back to "/") works on both routes.
 */
/**
 * STAFF SURFACE (Kelvo build step 2, item 5). A pod declaring surface 'staff'
 * gets its routes all behind Clerk: `/`, the staff chat; `/questions`, the
 * owner's queue of what staff asked and nobody had written down; `/documents`,
 * his files (step 3c); `/knows`, everything the assistant may say and the
 * drawer each is in (part 2c); `/renewals`, what the firm must renew and
 * by when (batch 2y); and `/report`, what staff asked that was not written
 * down, with the weekly text's switch (Build 10c/10e). Every other path redirects to `/`. The public chat, the Foredesk supplier
 * dashboard and the voice record page are simply not mounted — not hidden, not
 * guarded: absent from the bundle's routing, so there is nothing to find.
 */
const staffSurface = getActivePod().surface === 'staff'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      {staffSurface ? (
        // ONE ClerkProvider around the whole staff surface, so moving between
        // the chat and the owner's questions keeps the session in memory
        // instead of re-initialising Clerk on every navigation.
        <ClerkProvider publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}>
          <StaffConversationGuard />
          <Routes>
            <Route path="/" element={<StaffChat />} />
            <Route path="/questions" element={<OwnerQuestions />} />
            <Route path="/documents" element={<OwnerDocuments />} />
            <Route path="/knows" element={<OwnerFacts />} />
            <Route path="/renewals" element={<OwnerRenewals />} />
            <Route path="/report" element={<OwnerReport />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ClerkProvider>
      ) : (
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/record/:token" element={<Record />} />
        <Route
          path="/dashboard"
          element={
            <ClerkProvider publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}>
              <Dashboard />
            </ClerkProvider>
          }
        />
      </Routes>
      )}
    </BrowserRouter>
  </React.StrictMode>,
)
